// Personalization service for handling merge tags and dynamic content in email templates

export interface ContactField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'array';
  description: string;
}

export interface MergeTag {
  id: string;
  name: string;
  syntax: string;
  description: string;
  example: string;
}

export class PersonalizationService {
  // Standard contact fields that are always available
  static readonly STANDARD_FIELDS: ContactField[] = [
    { id: 'firstName', name: 'First Name', type: 'text', description: "Contact's first name" },
    { id: 'lastName', name: 'Last Name', type: 'text', description: "Contact's last name" },
    { id: 'fullName', name: 'Full Name', type: 'text', description: "Contact's full name" },
    { id: 'email', name: 'Email', type: 'text', description: "Contact's email address" },
    { id: 'company', name: 'Company', type: 'text', description: "Contact's company" },
    { id: 'position', name: 'Position', type: 'text', description: "Contact's position" },
    { id: 'currentDate', name: 'Current Date', type: 'date', description: 'Current date' },
    { id: 'currentTime', name: 'Current Time', type: 'text', description: 'Current time' },
    { id: 'currentDateTime', name: 'Current Date & Time', type: 'text', description: 'Current date and time' },
  ];

  // Standard merge tags that are always available
  static readonly STANDARD_TAGS: MergeTag[] = [
    { 
      id: 'firstName', 
      name: 'First Name', 
      syntax: '{{firstName}} or {{first_name}}', 
      description: "Insert contact's first name", 
      example: 'Hi {{firstName}},' 
    },
    { 
      id: 'lastName', 
      name: 'Last Name', 
      syntax: '{{lastName}} or {{last_name}}', 
      description: "Insert contact's last name", 
      example: 'Hi {{firstName}} {{lastName}},' 
    },
    { 
      id: 'fullName', 
      name: 'Full Name', 
      syntax: '{{fullName}} or {{full_name}}', 
      description: "Insert contact's full name", 
      example: 'Dear {{fullName}},' 
    },
    { 
      id: 'company', 
      name: 'Company', 
      syntax: '{{company}}', 
      description: "Insert contact's company", 
      example: 'We noticed {{company}} is in a growing industry.' 
    },
    { 
      id: 'position', 
      name: 'Position', 
      syntax: '{{position}}', 
      description: "Insert contact's position", 
      example: 'As a {{position}}, you might be interested in...' 
    },
    { 
      id: 'currentDate', 
      name: 'Current Date', 
      syntax: '{{currentDate}}', 
      description: 'Insert current date', 
      example: 'Today is {{currentDate}}.' 
    },
    { 
      id: 'conditional', 
      name: 'Conditional Block', 
      syntax: '{{#if condition}}content{{/if}}', 
      description: 'Show content only if condition is met', 
      example: '{{#if company}}Hello from {{company}}!{{/if}}' 
    },
    { 
      id: 'loop', 
      name: 'Loop Block', 
      syntax: '{{#each items}}content{{/each}}', 
      description: 'Repeat content for each item in a list', 
      example: '{{#each interests}}- {{.}}{{/each}}' 
    },
  ];

  /**
   * Get all available merge tags including custom fields
   * @param customFields Custom fields defined for the campaign
   * @returns Array of all available merge tags
   */
  static getAvailableTags(customFields: ContactField[] = []): MergeTag[] {
    // Combine standard tags with custom field tags
    const customTags = customFields.map(field => ({
      id: field.id,
      name: field.name,
      syntax: `{{${field.id}}}`,
      description: field.description,
      example: `{{${field.id}}}`
    }));
    
    return [...this.STANDARD_TAGS, ...customTags];
  }

  /**
   * Validate template content for proper merge tag syntax
   * @param content Template content to validate
   * @returns Validation result with any errors found
   */
  static validateTemplate(content: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // Check for unclosed conditional blocks
    const conditionalOpenCount = (content.match(/\{\{\s*#if\s+\w+\s*\}\}/g) || []).length;
    const conditionalCloseCount = (content.match(/\{\{\s*\/if\s*\}\}/g) || []).length;
    
    if (conditionalOpenCount !== conditionalCloseCount) {
      errors.push('Mismatched conditional block tags');
    }
    
    // Check for unclosed loop blocks
    const loopOpenCount = (content.match(/\{\{\s*#each\s+\w+\s*\}\}/g) || []).length;
    const loopCloseCount = (content.match(/\{\{\s*\/each\s*\}\}/g) || []).length;
    
    if (loopOpenCount !== loopCloseCount) {
      errors.push('Mismatched loop block tags');
    }
    
    // Check for invalid merge tag syntax
    const invalidTags = content.match(/\{\{[^}]*\}(?!\})/g);
    if (invalidTags) {
      errors.push('Invalid merge tag syntax detected');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Preview template with sample data
   * @param content Template content
   * @param sampleData Sample data to use for preview
   * @returns Processed content with sample data inserted
   */
  static previewTemplate(content: string, sampleData: Record<string, any>): string {
    let processedContent = content;
    
    // Replace standard merge tags
    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      processedContent = processedContent.replace(regex, String(value || ''));
    });
    
    // Handle conditional blocks (simplified for preview)
    processedContent = processedContent.replace(/\{\{\s*#if\s+(\w+)\s*\}\}([\s\S]*?)\{\{\s*\/if\s*\}\}/g, (match, condition, innerContent) => {
      // In preview, show content if condition key exists in sample data
      return sampleData[condition] ? innerContent : '';
    });
    
    // Handle loop blocks (simplified for preview)
    processedContent = processedContent.replace(/\{\{\s*#each\s+(\w+)\s*\}\}([\s\S]*?)\{\{\s*\/each\s*\}\}/g, (match, itemsKey, innerContent) => {
      const items = sampleData[itemsKey];
      
      if (Array.isArray(items)) {
        return items.map(item => {
          return innerContent.replace(/\{\{\s*\.\s*\}\}/g, String(item));
        }).join('');
      }
      
      return '';
    });
    
    return processedContent;
  }
}
